import React, { useEffect, useMemo, useState } from 'react'

const cleanImages = (images, fallbackImage = '') => {
  const list = Array.isArray(images)
    ? images
        .filter(
          (item) =>
            typeof item === 'string' &&
            item.trim()
        )
        .map((item) => item.trim())
    : []

  if (list.length > 0) {
    return [...new Set(list)]
  }

  if (
    fallbackImage &&
    typeof fallbackImage === 'string' &&
    fallbackImage.trim()
  ) {
    return [fallbackImage.trim()]
  }

  return []
}

const clampNumber = (value, min, max, fallback) => {
  const parsed = Number(value)

  if (!Number.isFinite(parsed)) {
    return fallback
  }

  return Math.max(min, Math.min(max, parsed))
}

const BackgroundSlideshow = ({
  images = [],
  fallbackImage = '',
  enabled = true,
  interval = 8000,
  transitionDuration = 1400,
  position = 'center center',
  positionMobile = null,
  overlayOpacity = 0,
}) => {
  const normalizedImages = useMemo(
    () => cleanImages(images, fallbackImage),
    [images, fallbackImage]
  )

  const [slideState, setSlideState] = useState({ current: 0, previous: null })
  const { current: currentIndex, previous: previousIndex } = slideState

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined'
      ? window.innerWidth < 768
      : false
  )

  // Reset to first image if the image list changes.
  useEffect(() => {
    setSlideState({ current: 0, previous: null })
  }, [normalizedImages.join('|')])

  // Avoid eager-loading every slide: the browser will fetch only rendered backgrounds.
  // Keep current, previous (for cross-fade) and next (for a smooth transition).
  // Detect mobile/desktop for background positioning.
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Automatic carousel rotation.
  useEffect(() => {
    if (
      !enabled ||
      normalizedImages.length <= 1
    ) {
      return undefined
    }

    const safeInterval = clampNumber(
      interval,
      3000,
      30000,
      8000
    )

    const timer = window.setInterval(() => {
      setSlideState((prev) => ({ previous: prev.current, current: (prev.current + 1) % normalizedImages.length }))
    }, safeInterval)

    return () => {
      window.clearInterval(timer)
    }
  }, [
    enabled,
    interval,
    normalizedImages.length,
  ])

  if (normalizedImages.length === 0) {
    return null
  }

  const safeTransition = clampNumber(
    transitionDuration,
    300,
    5000,
    1400
  )

  const safeOverlay =
    clampNumber(
      overlayOpacity,
      0,
      90,
      0
    ) / 100

  const activePosition =
    isMobile && positionMobile
      ? positionMobile
      : position

  return (
    <div className="aox-managed-background pointer-events-none absolute inset-0 z-0 overflow-hidden">

      {/* Only the active, preceding and upcoming backgrounds are mounted. */}
      {normalizedImages.map((image, index) => ({ image, index }))
        .filter(({ index }) => index === currentIndex || index === previousIndex || index === (currentIndex + 1) % normalizedImages.length)
        .map(({ image, index }) => (
        <div
          key={image}
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${JSON.stringify(image)})`,
            backgroundPosition:
              activePosition || 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',

            opacity:
              index === currentIndex ? 1 : 0,

            transition: `opacity ${safeTransition}ms ease-in-out`,

            willChange: 'opacity',
          }}
        />
      ))}

      {/* Optional dark overlay */}
      {safeOverlay > 0 && (
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: `rgba(0, 0, 0, ${safeOverlay})`,
          }}
        />
      )}

    </div>
  )
}

export default BackgroundSlideshow